// Filename: TextField.tsx
//
// Component: TextField
//
// Purpose:
// Provide a reusable TextField component based on MUI OutlinedInput.
// Supports optional password mode with a visibility toggle,
// plus optional error state and helper text.
//
// What this file does:
// - Uses MUI FormControl + InputLabel + OutlinedInput for consistent forms.
// - If `password` is true, renders a visibility toggle icon button.
// - Calls the provided `onChange` callback whenever the input value changes.
// - Displays helper text and error styling when error is true.

import { Visibility, VisibilityOff } from "@mui/icons-material"; // Icons for show/hide password
import { useTextFieldStyles } from "./TextFieldStyles"; // Style hook for sx styling
import {
  FormControl,
  InputAdornment,
  IconButton,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material"; // MUI form components
import { useState } from "react"; // React state for password visibility toggle

// TextFieldProps
// Defines the props supported by this TextField component.
interface TextFieldProps {
  label?: string; // Label shown above the input
  password?: boolean; // If true, input behaves like a password field
  onChange: (value: string) => void; // Called with the current value when the input changes
  error?: boolean; // If true, displays error styles
  helperText?: string; // Optional helper/error message below the field
}

// TextField component
const TextField = ({
  label,
  password,
  onChange,
  error,
  helperText,
}: TextFieldProps) => {
  // State controlling whether password is visible (only relevant when password=true)
  const [showPassword, setShowPassword] = useState(false);

  // Toggles password visibility on icon click
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  // Get component styles (sx objects)
  const {classes} = useTextFieldStyles();

  return (
    // FormControl wraps input + label + helper text in a consistent structure
    <FormControl size="small">
      {/* InputLabel shows above the input; error prop applies red styling if needed */}
      <InputLabel error={error}>{label}</InputLabel>

      {/* OutlinedInput is the actual input element */}
      <OutlinedInput
        // Label value must match InputLabel for proper outline/float behavior
        label={label ? label : ""}

        // Apply custom sx styles from the styles hook
        className={classes.default}
        // Determine the input type:
        // - If password mode: "password" by default, "text" when showPassword is true
        // - Otherwise: standard "text"
        type={password ? (showPassword ? "text" : "password") : "text"}
        size="small"
        // When user types, call onChange with the string value
        onChange={(e) => onChange && onChange(e.target.value)}
        // Enable error styling if error is true
        error={error}
        // Conditionally render the endAdornment (eye icon) only when password mode is enabled
        endAdornment={
          password && (
            <InputAdornment position="end">
              <IconButton
                // Accessibility label changes depending on state
                aria-label={
                  showPassword ? "hide the password" : "display the password"
                }
                // Toggle show/hide password
                onClick={handleClickShowPassword}
                edge="end"
              >
                {/* Pick icon based on visibility state */}
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }
      />

      {/* Helper text appears below input; styled as error when error is true */}
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default TextField;
